import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Canvas, { ChartActivePoint } from "../components/chart.js";
import DashboardDonut from "../components/dashboardDonut.js";
import DashboardHeatmap from "../components/dashboardHeatmap.js";
import { useFetch } from "../utils.js";
import { useContextMenuProvider } from "../components/contextMenuProvider.js";
import {
    DrilldownResult,
    ExportMode,
    ExportPreviewTab,
    SchemaCountByTimeRangeType,
    SchemaResultCountByTimeRange,
    SchemaResultType,
    StatsElementType,
} from "./dashboard/model.js";
import {
    downloadCsv,
    fromDateTimeLocal,
    getStatsExportRows as buildStatsExportRows,
    toDateTimeLocal,
} from "./dashboard/export.js";
import {
    fetchMissingEntityLabels,
    getCachedEntityLabel,
    getDrilldown,
} from "./dashboard/query.js";
import {
    resolveDashboardHeatmapOptions,
    resolveDashboardHeatmapWindowBefore,
} from "./dashboard/heatmap.js";

export type {
    DrilldownResult,
    ExportMode,
    ExportPreviewTab,
    SchemaCountByTimeRangeType,
    SchemaResultCountByTimeRange,
    SchemaResultType,
    StatsElementType,
} from "./dashboard/model.js";

function useInterval(ms: number, cb: () => void | Promise<void>) {
    const tid = ref<NodeJS.Timeout | undefined>();

    onMounted(() => {
        tid.value = setInterval(cb, ms);
    });

    onUnmounted(() => {
        clearInterval(tid.value);
    });
}

function sumValues(values: number[]) {
    return values.reduce((sum, value) => sum + value, 0);
}

function formatCompactNumber(value: number) {
    return new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: value >= 100 ? 0 : 1,
        notation: value >= 10_000 ? "compact" : "standard",
    }).format(value);
}

function calculateTrend(values: number[]) {
    if (values.length < 2)
        return null;

    const midpoint = Math.max(1, Math.floor(values.length / 2));
    const previous = sumValues(values.slice(0, midpoint));
    const current = sumValues(values.slice(midpoint));

    if (previous === 0 && current === 0)
        return {
            percent : 0,
            delta   : 0,
        };

    if (previous === 0)
        return {
            percent : 100,
            delta   : current,
        };

    return {
        percent : ((current - previous) / previous) * 100,
        delta   : current - previous,
    };
}

function calculateLastPointTrend(values: number[]) {
    const current = values.at(-1) || 0;
    const previous = values.at(-2) || 0;
    const delta = current - previous;

    if (previous === 0 && current === 0)
        return {
            current,
            previous,
            delta,
            percent: 0,
        };

    if (previous === 0)
        return {
            current,
            previous,
            delta,
            percent: 100,
        };

    return {
        current,
        previous,
        delta,
        percent: (delta / previous) * 100,
    };
}

function getTrendClass(trend: ReturnType<typeof calculateTrend>) {
    if (!trend || trend.delta === 0)
        return "neutral";

    return trend.delta > 0 ? "positive" : "negative";
}

function getTrendIcon(trendClass: string) {
    if (trendClass === "positive")
        return "trending_up";

    if (trendClass === "negative")
        return "trending_down";

    return "trending_flat";
}

export default defineComponent({
    props: {
        index: {
            type        : Number,
            required    : true,
        },
        element: {
            type: Object as () => StatsElementType,
            required: true,
        },
        timeRange: {
            type        : Number,
            required    : true,
        },
        visiblePoints: {
            type        : Number,
            required    : true,
        },
    },

    setup(props) {
        const { fetch }         = useFetch();
        const router            = useRouter();
        const contextMenu       = useContextMenuProvider();
        const refResult         = ref<SchemaResultType | undefined>();
        const heatmapOptions = computed(() => resolveDashboardHeatmapOptions(props.element.heatmap));
        const effectiveTimeRange = computed(() => props.element.graphType === "heatmap" ? heatmapOptions.value.bucketMs : props.timeRange);
        const effectiveVisiblePoints = computed(() => props.element.graphType === "heatmap" ? heatmapOptions.value.cellCount : props.visiblePoints);
        const refAfter          = computed(() => Date.now() - (effectiveTimeRange.value * effectiveVisiblePoints.value));
        const refHeatmapBefore  = ref(Date.now());
        const refLoading        = ref(false);
        const refExpanded       = ref(false);
        const refExportOpen     = ref(false);
        const refExportMode     = ref<ExportMode>("stats");
        const refExportFrom     = ref(toDateTimeLocal(refAfter.value));
        const refExportTo       = ref(toDateTimeLocal(Date.now()));
        const refExportBucket   = ref(effectiveTimeRange.value);
        const refExportStatsRows = ref<unknown[][] | null>(null);
        const refExportDrilldownRows = ref<unknown[][] | null>(null);
        const refExportPreviewTab = ref<ExportPreviewTab>("stats");
        const refExportLoading  = ref(false);
        const refExportError    = ref<string | null>(null);
        const refDrilldown      = ref<DrilldownResult | null>(null);
        const refDrilldownLabels = ref<Map<string, string>>(new Map());
        const refRankingLabels = ref<Map<string, string>>(new Map());
        const refActiveDrilldownKey = ref<string | null>(null);
        const hasConfiguredDrilldown = computed(() => {
            return !!props.element.datasets?.some((dataset) => dataset.drilldown?.enabled !== false);
        });

        async function updateElement() {
            const element = props.element;

            refLoading.value = true;

            try {
                const heatmap = element.graphType === "heatmap";
                const before = heatmap
                    ? resolveDashboardHeatmapWindowBefore(Date.now(), effectiveTimeRange.value)
                    : Date.now();
                const after = heatmap
                    ? before - effectiveTimeRange.value * effectiveVisiblePoints.value
                    : refAfter.value;

                if (heatmap)
                    refHeatmapBefore.value = before;

                const response = await fetch(`/api/dashboard-stats?index=${props.index}&timeRange=${effectiveTimeRange.value}&after=${after}&before=${before}`, { method: "POST" })
                    .then((res) => res.json());

                const res = response.result;

                if (res.values) {
                    if (element.schema[1]!.type === "count_by_time_range") {

                        if (element.graphType !== "ranking") {
                            for (const value of Object.values(res.values) as number[][]) {
                                const missingValues = Math.max(0, (effectiveVisiblePoints.value - value.length));

                                for (let i = 0; i < missingValues; i++)
                                    props.element.graphType === "heatmap" ? value.unshift(0) : value.push(0);
                            }
                        } else {
                            const ids = Object.keys(res.values);
                            const labelKind = element.ranking?.groupBy === "channel" ? "channel" : "member";

                            await fetchMissingEntityLabels(fetch, labelKind, ids).catch(() => undefined);
                            refRankingLabels.value = new Map(ids.map((id) => [
                                id,
                                getCachedEntityLabel(labelKind, id) || id,
                            ]));
                        }
                        // We don't do anything.

                    // } else if (element.schema[1]!.property === "channel") {
                    //     const channelIds    = res.values.map((entry: any) => entry[0]);
                    //     const channels      = await fetch("/api/channels", {
                    //         headers: {
                    //             "Content-Type": "application/json",
                    //         },
                    //         body    : JSON.stringify(channelIds),
                    //         method  : "POST",
                    //     })
                    //         .then((res) => res.json())
                    //         .then((res) => new Map(res.map((channel: any) => [ channel.id, channel ])));

                    //     for (let i = 0; i <  res.values.length; i++) {
                    //         const [ channelId, count ] = res.values[i];

                    //         res.values[i] = {
                    //             channel: channels.get(channelId) ?? { name: "Salon supprimé" },
                    //             count,
                    //         };
                    //     }

                    } else {
                        const userIds   = (res.values as [string, number][]).map((entry) => entry[0]);
                        const members   = await fetch("/api/members", {
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body    : JSON.stringify(userIds),
                            method  : "POST",
                        })
                            .then((res) => res.json())
                            .then((entries: Array<{ id: string }>) => new Map(entries.map((entry) => [ entry.id, entry ])));

                        for (let i = 0; i <  res.values.length; i++) {
                            const [ userId, count ] = res.values[i];

                            res.values[i] = {
                                member: members.get(userId),
                                count,
                            };
                        }
                    }
                }

                refResult.value = res;

            } finally {
                refLoading.value = false;
            }
        }

        onMounted(updateElement);
        watch(refAfter, updateElement);
        watch(hasConfiguredDrilldown, (enabled) => {
            if (!enabled && refExportMode.value !== "stats")
                refExportMode.value = "stats";
        }, { immediate: true });
        useInterval(60_000, updateElement);

        function generateElementLabels(result: SchemaResultCountByTimeRange) {
            const currentDate   = new Date();
            const timeRange     = effectiveTimeRange.value;
            const limit         = Object.values(result.values)[0]!.length;
            const labels        = [];

            // Commencer par arrondir la date actuelle au prochain chunk (plafond)
            const startDate = Math.floor(currentDate.getTime() / timeRange) * timeRange;

            // Générer les labels en partant du plus ancien chunk
            for (let i = 0; i < limit; i++) {
                if (i === limit - 1) {
                    // Le dernier label est la date actuelle (non arrondie)
                    labels.push(currentDate.toLocaleString());

                } else {
                    // Les autres labels sont arrondis au début de chaque chunk
                    labels.push(new Date(startDate - (timeRange * (limit - i - 1))).toLocaleString());
                }
            }

            return labels;
        }

        async function loadDrilldown(point: ChartActivePoint | null) {
            if (!point || !props.element.datasets?.length)
                return;

            const dataset = props.element.datasets[point.datasetIndex];

            if (!dataset)
                return;

            const windowAfter = props.element.graphType === "heatmap"
                ? refHeatmapBefore.value - effectiveTimeRange.value * effectiveVisiblePoints.value
                : refAfter.value;
            const after = windowAfter + point.index * effectiveTimeRange.value;
            const before = after + effectiveTimeRange.value;
            const drilldownKey = `${props.index}:${dataset.name}:${after}:${before}`;

            if (refActiveDrilldownKey.value === drilldownKey)
                return;

            refActiveDrilldownKey.value = drilldownKey;

            const params = new URLSearchParams({
                index   : String(props.index),
                dataset : dataset.name,
                after   : String(after),
                before  : String(before),
            });

            const drilldown = await getDrilldown(fetch, drilldownKey, `/api/dashboard-stats/drilldown?${params}`);

            if (refActiveDrilldownKey.value !== drilldownKey)
                return;

            refDrilldown.value = drilldown;

            const ids = drilldown.result?.map((entry: { id: string }) => entry.id) || [];

            if (!ids.length)
                return;

            const labelKind = drilldown.groupBy === "channel" ? "channel" : "member";

            await fetchMissingEntityLabels(fetch, labelKind, ids);

            if (refActiveDrilldownKey.value !== drilldownKey)
                return;

            refDrilldownLabels.value = new Map(ids.map((id) => [
                id,
                getCachedEntityLabel(labelKind, id) || id,
            ]));
        }

        function renderDrilldownTable() {
            if (!refDrilldown.value)
                return (
                    <div class="dashboard-card-content">
                        Survole une période du graphique pour afficher les données associées.
                    </div>
                );

            return (
                <div class="dashboard-drilldown-table">
                    <div class="dashboard-drilldown-row dashboard-drilldown-head">
                        <div>{refDrilldown.value.groupBy}</div>
                        <div>Occurrences</div>
                        <div>Première occurrence</div>
                        <div>Dernière occurrence</div>
                    </div>

                    {refDrilldown.value.result.map((entry) => (
                        <div class="dashboard-drilldown-row">
                            <div>{refDrilldownLabels.value.get(entry.id) || entry.id}</div>
                            <div>{entry.count}</div>
                            <div>{new Date(entry.firstSeenAt).toLocaleString()}</div>
                            <div>{new Date(entry.lastSeenAt).toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            );
        }

        function getStatsExportRows(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType, after = refAfter.value, timeRange = effectiveTimeRange.value) {
            if (props.element.graphType === "ranking") {
                const groupLabel = props.element.ranking?.groupBy === "channel"
                    ? "Salon"
                    : props.element.ranking?.groupBy === "target" ? "Cible" : "Exécuteur";
                const entries = Object.entries(result.values)
                    .map(([ id, values ]) => ({ id, count: sumValues(values) }))
                    .sort((left, right) => right.count - left.count);

                return [
                    [ "Position", groupLabel, "Identifiant", "Occurrences" ],
                    ...entries.map((entry, index) => [
                        index + 1,
                        refRankingLabels.value.get(entry.id) || entry.id,
                        entry.id,
                        entry.count,
                    ]),
                ];
            }

            return buildStatsExportRows(result, timeElement, after, timeRange);
        }

        async function getBucketDrilldownExportRows(after: number, before: number, timeRange: number) {
            const rows: unknown[][] = [[ "Période", "Dataset", "Groupe", "ID", "Occurrences", "Première occurrence", "Dernière occurrence" ]];
            const datasets = (props.element.datasets || []).filter((dataset) => dataset.drilldown?.enabled !== false);
            const bucketCount = Math.ceil((before - after) / timeRange);

            if (!datasets.length)
                throw new Error("Ce graph n'a pas de drilldown configuré. Modifie ou crée un dashboard custom pour définir les séries et le groupement drilldown.");

            for (let bucketIndex = 0; bucketIndex < bucketCount; bucketIndex++) {
                const bucketAfter = after + bucketIndex * timeRange;
                const bucketBefore = Math.min(before, bucketAfter + timeRange);
                const periodLabel = `${new Date(bucketAfter).toLocaleString()} → ${new Date(bucketBefore).toLocaleString()}`;

                for (const dataset of datasets) {
                    const params = new URLSearchParams({
                        index   : String(props.index),
                        dataset : dataset.name,
                        after   : String(bucketAfter),
                        before  : String(bucketBefore),
                    });
                    const drilldownKey = `${props.index}:${dataset.name}:${bucketAfter}:${bucketBefore}`;
                    const drilldown = await getDrilldown(fetch, drilldownKey, `/api/dashboard-stats/drilldown?${params}`);
                    const ids = drilldown.result.map((entry) => entry.id);
                    const labelKind = drilldown.groupBy === "channel" ? "channel" : "member";

                    await fetchMissingEntityLabels(fetch, labelKind, ids);

                    for (const entry of drilldown.result) {
                        rows.push([
                            periodLabel,
                            dataset.label,
                            getCachedEntityLabel(labelKind, entry.id) || entry.id,
                            entry.id,
                            entry.count,
                            new Date(entry.firstSeenAt).toLocaleString(),
                            new Date(entry.lastSeenAt).toLocaleString(),
                        ]);
                    }
                }
            }

            return rows;
        }

        async function prepareExportRows(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            refExportLoading.value = true;
            refExportError.value = null;
            refExportStatsRows.value = null;
            refExportDrilldownRows.value = null;

            try {
                const after = fromDateTimeLocal(refExportFrom.value);
                const before = fromDateTimeLocal(refExportTo.value);
                const timeRange = refExportBucket.value;

                if (after >= before) {
                    refExportError.value = "La date de début doit être avant la date de fin.";
                    return;
                }

                const bucketCount = Math.ceil((before - after) / timeRange);

                if (bucketCount > 100) {
                    refExportError.value = "L'export est limité à 100 buckets. Augmente la fréquence ou réduis la période.";
                    return;
                }

                if ([ "bucket_drilldown", "both" ].includes(refExportMode.value) && !hasConfiguredDrilldown.value) {
                    refExportError.value = "Ce graph n'a pas de drilldown configuré. Utilise un dashboard custom pour exporter le drilldown.";
                    return;
                }

                let exportResult = result;

                if ([ "stats", "both" ].includes(refExportMode.value)) {
                    const params = new URLSearchParams({
                        index       : String(props.index),
                        timeRange   : String(timeRange),
                        after       : String(after),
                        before      : String(before),
                    });
                    const response = await fetch(`/api/dashboard-stats?${params}`, { method: "POST" })
                        .then((res) => res.json());

                    exportResult = response.result;
                    refExportStatsRows.value = getStatsExportRows(exportResult, timeElement, after, timeRange);
                    refExportPreviewTab.value = "stats";
                }

                if ([ "bucket_drilldown", "both" ].includes(refExportMode.value))
                    refExportDrilldownRows.value = await getBucketDrilldownExportRows(after, before, timeRange);

                if (refExportMode.value === "bucket_drilldown")
                    refExportPreviewTab.value = "drilldown";

            } catch (error) {
                refExportError.value = error instanceof Error ? error.message : "Impossible de préparer l'export.";

            } finally {
                refExportLoading.value = false;
            }
        }

        function renderExportPreview(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            const statsRows = refExportStatsRows.value || getStatsExportRows(result, timeElement);
            const drilldownRows = refExportDrilldownRows.value;
            const canShowStats = refExportMode.value !== "bucket_drilldown";
            const canShowDrilldown = refExportMode.value !== "stats" && hasConfiguredDrilldown.value;
            const rows = (
                refExportPreviewTab.value === "drilldown"
                    ? (drilldownRows || [[ "Prépare l'export pour voir le drilldown par période." ]])
                    : statsRows
            );
            const columnCount = Math.max(1, ...rows.map((row) => row.length));

            return (
                <>
                    <div class="dashboard-export-tabs">
                        {canShowStats && (
                            <button class={{ "dashboard-export-tab": true, "dashboard-export-tab-active": refExportPreviewTab.value === "stats" }} onClick={() => refExportPreviewTab.value = "stats"}>
                                Stats
                            </button>
                        )}

                        {canShowDrilldown && (
                            <button class={{ "dashboard-export-tab": true, "dashboard-export-tab-active": refExportPreviewTab.value === "drilldown" }} onClick={() => refExportPreviewTab.value = "drilldown"}>
                                Drilldown par période
                            </button>
                        )}
                    </div>

                    <div class="dashboard-export-preview">
                        <div class="dashboard-export-table" style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(160px, 1fr))` }}>
                            {rows.map((row, rowIndex) => (
                                <>
                                    {Array.from({ length: columnCount }, (_, cellIndex) => (
                                        <div class={{ "dashboard-export-cell": true, "dashboard-export-head": rowIndex === 0 }}>
                                            {String(row[cellIndex] ?? "")}
                                        </div>
                                    ))}
                                </>
                            ))}
                        </div>
                    </div>
                </>
            );
        }

        function exportCsv(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            const baseName = `${props.element.label.replace(/[^a-z0-9_-]+/gi, "_").toLowerCase()}-${Date.now()}`;
            const statsRows = refExportStatsRows.value || getStatsExportRows(result, timeElement);

            if (refExportMode.value === "stats")
                downloadCsv(`${baseName}-stats.csv`, statsRows);
            else if (refExportMode.value === "bucket_drilldown")
                downloadCsv(`${baseName}-drilldown.csv`, refExportDrilldownRows.value || [[ "Prépare l'export avant téléchargement." ]]);
            else {
                downloadCsv(`${baseName}-stats.csv`, statsRows);
                downloadCsv(`${baseName}-drilldown.csv`, refExportDrilldownRows.value || [[ "Prépare l'export avant téléchargement." ]]);
            }

            refExportOpen.value = false;
        }

        async function deleteDashboard() {
            if (!props.element.id)
                return;

            if (!confirm(`Supprimer le graph "${props.element.label}" ?`))
                return;

            const res = await fetch(`/api/dashboards/${props.element.id}`, {
                method: "DELETE",
            });

            if (res.status === 204)
                location.reload();
        }

        function displayGraphMenu(event: MouseEvent) {
            event.preventDefault();
            event.stopImmediatePropagation();

            contextMenu.displayContextMenu({
                origin: {
                    x: event.clientX,
                    y: event.clientY,
                },
                items: [
                    ...(props.element.id ? [
                        {
                            icon    : () => <i class="material-symbols-outlined">edit</i>,
                            label   : () => "Modifier",
                            onClick : () => router.push(`/dashboards/${props.element.id}/edit`),
                        },
                        {
                            icon        : () => <i class="material-symbols-outlined">delete</i>,
                            label       : () => "Supprimer",
                            classNames  : "dangerous",
                            onClick     : deleteDashboard,
                        },
                    ] : []),
                    {
                        icon    : () => <i class="material-symbols-outlined">download</i>,
                        label   : () => "Exporter",
                        onClick : () => refExportOpen.value = true,
                    },
                ],
            });
        }

        function renderChart(element: StatsElementType, result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType, expanded = false) {
            return (
                <Canvas
                    onActivePoint={expanded ? loadDrilldown : undefined}
                    options={{
                        visiblePoints   : effectiveVisiblePoints.value,
                        graphType       : [ "bar", "stacked-bar" ].includes(element.graphType || "") ? "bar" : "line",
                        stacked         : element.graphType === "stacked-bar",
                        labels          : generateElementLabels(result),
                        datasets: Object.entries(result.values).map(([ name, values ]) => ({
                            label   : timeElement.properties.find((property) => (property.name === name))?.label || name,
                            data    : values,
                            color   : timeElement.properties.find((property) => (property.name === name))!.color,
                        })),
                    }}
                />
            );
        }

        function renderMetric(element: StatsElementType, result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            const series = Object.entries(result.values).map(([ name, values ]) => {
                const property = timeElement.properties.find((entry) => entry.name === name);

                return {
                    name,
                    label   : property?.label || name,
                    color   : property?.color || "rgb(107, 229, 190)",
                    values,
                    total   : sumValues(values),
                    trend   : calculateTrend(values),
                };
            });
            const total = sumValues(series.map((entry) => entry.total));
            const mergedValues = result.values[series[0]?.name || ""]?.map((_, index) => {
                return series.reduce((sum, entry) => sum + (entry.values[index] || 0), 0);
            }) || [];
            const trend = calculateTrend(mergedValues);
            const trendClass = getTrendClass(trend);

            return (
                <div class="dashboard-metric">
                    <div class="dashboard-metric-main">
                        <span>Total période</span>
                        <strong>{formatCompactNumber(total)}</strong>
                        <small class={`dashboard-metric-trend ${trendClass}`}>
                            <i class="material-symbols-outlined">
                                {getTrendIcon(trendClass)}
                            </i>
                            {trend ? `${trend.delta >= 0 ? "+" : ""}${formatCompactNumber(trend.delta)} · ${trend.percent >= 0 ? "+" : ""}${trend.percent.toFixed(1)}%` : "Évolution indisponible"}
                        </small>
                    </div>

                    <div class="dashboard-metric-sparkline">
                        {renderChart(element, result, timeElement)}
                    </div>

                    <div class="dashboard-metric-series">
                        {series.map((entry) => (
                            <div class="dashboard-metric-series-item">
                                <i style={{ background: entry.color }} />
                                <span>{entry.label}</span>
                                <strong>{formatCompactNumber(entry.total)}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        function renderCounter(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            const series = Object.entries(result.values).map(([ name, values ]) => {
                const property = timeElement.properties.find((entry) => entry.name === name);
                const trend = calculateLastPointTrend(values);

                return {
                    name,
                    label       : property?.label || name,
                    color       : property?.color || "rgb(107, 229, 190)",
                    current     : trend.current,
                    previous    : trend.previous,
                    trend,
                };
            });
            const current = sumValues(series.map((entry) => entry.current));
            const previous = sumValues(series.map((entry) => entry.previous));
            const trend = calculateLastPointTrend([ previous, current ]);
            const trendClass = getTrendClass(trend);

            return (
                <div class="dashboard-counter">
                    <div class="dashboard-counter-main">
                        <span>Dernier point</span>
                        <strong>{formatCompactNumber(current)}</strong>
                        <small class={`dashboard-metric-trend ${trendClass}`}>
                            <i class="material-symbols-outlined">{getTrendIcon(trendClass)}</i>
                            {`${trend.delta >= 0 ? "+" : ""}${formatCompactNumber(trend.delta)} · ${trend.percent >= 0 ? "+" : ""}${trend.percent.toFixed(1)}%`}
                        </small>
                    </div>

                    <div class="dashboard-counter-context">
                        <div>
                            <span>Point précédent</span>
                            <strong>{formatCompactNumber(previous)}</strong>
                        </div>
                        <div>
                            <span>Séries</span>
                            <strong>{series.length}</strong>
                        </div>
                    </div>

                    <div class="dashboard-metric-series">
                        {series.map((entry) => (
                            <div class="dashboard-metric-series-item">
                                <i style={{ background: entry.color }} />
                                <span>{entry.label}</span>
                                <strong>{formatCompactNumber(entry.current)}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        function renderHeatmap(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType, expanded = false) {
            const [ name, values ] = Object.entries(result.values)[0] || [ "series_1", [] ];
            const property = timeElement.properties.find((entry) => entry.name === name);

            return (
                <DashboardHeatmap
                    values={values}
                    color={property?.color || "rgb(107, 229, 190)"}
                    label={property?.label || name}
                    before={refHeatmapBefore.value}
                    bucketMs={heatmapOptions.value.bucketMs}
                    cellCount={heatmapOptions.value.cellCount}
                    scale={heatmapOptions.value.scale}
                    onselect={expanded ? (index) => loadDrilldown({ datasetIndex: 0, index }) : undefined}
                />
            );
        }

        function renderDonut(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            return (
                <DashboardDonut entries={Object.entries(result.values).map(([ name, values ]) => {
                    const property = timeElement.properties.find((entry) => entry.name === name);

                    return {
                        label : property?.label || name,
                        value : sumValues(values),
                        color : property?.color || "rgb(107, 229, 190)",
                    };
                })} />
            );
        }

        function renderRanking(result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            const color = timeElement.properties[0]?.color || "rgb(107, 229, 190)";
            const entries = Object.entries(result.values)
                .map(([ id, values ]) => ({ id, count: sumValues(values) }))
                .sort((left, right) => right.count - left.count)
                .slice(0, props.element.ranking?.limit || 10);
            const maximum = entries[0]?.count || 1;

            return (
                <div class="dashboard-ranking">
                    {entries.map((entry, index) => (
                        <div class="dashboard-ranking-row" key={entry.id}>
                            <span class="dashboard-ranking-position">{index + 1}</span>
                            <div class="dashboard-ranking-value">
                                <span>{refRankingLabels.value.get(entry.id) || entry.id}</span>
                                <div class="dashboard-ranking-track">
                                    <i style={{
                                        "--ranking-color": color,
                                        background: color,
                                        inlineSize: `${Math.max(4, entry.count / maximum * 100)}%`,
                                    }} />
                                </div>
                            </div>
                            <strong>{formatCompactNumber(entry.count)}</strong>
                        </div>
                    ))}
                </div>
            );
        }

        function renderDashboardVisualization(element: StatsElementType, result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            if (element.graphType === "heatmap")
                return renderHeatmap(result, timeElement);

            if (element.graphType === "ranking")
                return renderRanking(result, timeElement);

            if (element.graphType === "donut")
                return renderDonut(result, timeElement);

            if (element.graphType === "metric")
                return renderMetric(element, result, timeElement);

            if (element.graphType === "counter")
                return renderCounter(result, timeElement);

            return renderChart(element, result, timeElement);
        }

        function renderExpandedVisualization(element: StatsElementType, result: SchemaResultCountByTimeRange, timeElement: SchemaCountByTimeRangeType) {
            if (element.graphType === "heatmap")
                return renderHeatmap(result, timeElement, true);

            if (element.graphType === "ranking")
                return renderRanking(result, timeElement);

            if (element.graphType === "donut")
                return renderDonut(result, timeElement);

            if (element.graphType === "counter")
                return renderCounter(result, timeElement);

            if (element.graphType === "metric")
                return renderMetric(element, result, timeElement);

            return renderChart(element, result, timeElement, true);
        }

        return () => {
            const element   = props.element;
            const result    = refResult.value;
            const loading   = refLoading.value;

            if (!result || loading)
                return (
                    <div class="dashboard-item-card">
                        <div class="dashboard-card-label">
                            {element.label}
                        </div>

                        <div class="dashboard-card-content">
                            Chargement en cours...
                        </div>
                    </div>
                );

            switch (result.type) {
                case "count":
                    return (
                        <div class="dashboard-item-card">
                            <div class="dashboard-card-label">
                                {element.label}
                            </div>

                            <div class="dashboard-card-content">
                                {result.count} résultats.
                            </div>
                        </div>
                    );

                // case "count_distinct":

                //     function renderList(element: StatsElementType) {
                //         if (element.schema[1]!.property === "channel") {
                //             return result.values.slice(0, 25).map(({ channel, count }) => (
                //                 <div class="dashboard-card-list-item">
                //                     <div class="dashboard-card-list-item-name">
                //                         {channel.name}
                //                     </div>

                //                     <div class="dashboard-card-list-item-value">
                //                         {count}
                //                     </div>
                //                 </div>
                //             ));

                //         } else {
                //             return result.values.slice(0, 25).map(({ member, count }) => (
                //                 <div class="dashboard-card-list-item">
                //                     <div class="dashboard-card-list-item-name">
                //                         <ImageFallback class="dashboard-member-avatar" src={getMemberAvatar(member)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(member)} />
                //                         {getMemberDisplayName(member)}
                //                     </div>

                //                     <div class="dashboard-card-list-item-value">
                //                         {count}
                //                     </div>
                //                 </div>
                //             ));
                //         }
                //     }

                //     return (
                //         <div class="dashboard-item-card">
                //             <div class="dashboard-card-label">
                //                 {element.label}
                //             </div>

                //             <div class="dashboard-card-list">
                //                 {renderList(element)}
                //             </div>

                //             <div class="dashboard-card-footer">
                //                 {result.values.length} résultats en total.
                //             </div>
                //         </div>
                //     );

                case "count_by_time_range":
                    const timeElement = element.schema[1] as SchemaCountByTimeRangeType;

                    return (
                        <div class="dashboard-item-card">
                            <div class="dashboard-card-header">
                                <div class="dashboard-card-label">
                                    {element.label}
                                </div>

	                                <div class="dashboard-card-header-actions">
	                                    <div class="dashboard-card-header-action" onClick={() => refExportOpen.value = true}>
	                                        <i class="material-symbols-outlined">download</i>
	                                    </div>

	                                    <div class="dashboard-card-header-action" onClick={() => refExpanded.value = true}>
	                                        <i class="material-symbols-outlined">fullscreen</i>
	                                    </div>

	                                    <div class="dashboard-card-header-action" onClick={displayGraphMenu}>
	                                        <i class="material-symbols-outlined">more_vert</i>
	                                    </div>
	                                </div>
                            </div>

	                            <div class="dashboard-card-list">
	                                {renderDashboardVisualization(element, result, timeElement)}
	                            </div>

	                            {refExportOpen.value && (
	                                <div class="dashboard-export-modal">
	                                    <div class="dashboard-export-inner">
	                                        <div class="dashboard-card-header">
	                                            <div class="dashboard-card-label">
	                                                Exporter {element.label}
	                                            </div>

	                                            <div class="dashboard-card-header-actions">
	                                                <div class="dashboard-card-header-action" onClick={() => refExportOpen.value = false}>
	                                                    <i class="material-symbols-outlined">close</i>
	                                                </div>
	                                            </div>
	                                        </div>

		                                        <div class="dashboard-export-options dashboard-export-options-grid">
		                                            <label class="dashboard-builder-field">
		                                                <span>Début</span>
		                                                <input type="datetime-local" value={refExportFrom.value} onInput={(event) => refExportFrom.value = (event.target as HTMLInputElement).value} />
		                                            </label>

		                                            <label class="dashboard-builder-field">
		                                                <span>Fin</span>
		                                                <input type="datetime-local" value={refExportTo.value} onInput={(event) => refExportTo.value = (event.target as HTMLInputElement).value} />
		                                            </label>

		                                            <label class="dashboard-builder-field">
		                                                <span>Fréquence</span>
		                                                <select value={refExportBucket.value} onChange={(event) => refExportBucket.value = Number((event.target as HTMLSelectElement).value)}>
		                                                    <option value={60_000}>Minute</option>
		                                                    <option value={10 * 60_000}>10 minutes</option>
		                                                    <option value={60 * 60_000}>Heure</option>
		                                                    <option value={24 * 60 * 60_000}>Jour</option>
		                                                    <option value={7 * 24 * 60 * 60_000}>Semaine</option>
		                                                </select>
		                                            </label>

		                                            <label class="dashboard-builder-field">
		                                                <span>Contenu</span>
		                                                <select value={refExportMode.value} onChange={(event) => refExportMode.value = (event.target as HTMLSelectElement).value as ExportMode}>
		                                                    <option value="stats">Stats agrégées</option>
		                                                    {hasConfiguredDrilldown.value && (
		                                                        <option value="bucket_drilldown">Drilldown par période</option>
		                                                    )}
		                                                    {hasConfiguredDrilldown.value && (
		                                                        <option value="both">Deux fichiers : stats + drilldown</option>
		                                                    )}
		                                                </select>
		                                            </label>

		                                            <button class="dashboard-option-button" onClick={() => prepareExportRows(result, timeElement)}>
		                                                <i class="material-symbols-outlined">preview</i>
		                                                {refExportLoading.value ? "Préparation..." : "Préparer"}
		                                            </button>

		                                            <button class="dashboard-option-button" onClick={() => exportCsv(result, timeElement)}>
		                                                <i class="material-symbols-outlined">download</i>
		                                                Télécharger CSV
		                                            </button>
		                                        </div>

		                                        {refExportError.value && (
		                                            <div class="dashboard-builder-error">
		                                                {refExportError.value}
		                                            </div>
		                                        )}

	                                        {renderExportPreview(result, timeElement)}
	                                    </div>
	                                </div>
	                            )}

	                            {refExpanded.value && (
	                                <div class="dashboard-expanded">
                                    <div class="dashboard-expanded-inner">
                                        <div class="dashboard-card-header">
                                            <div class="dashboard-card-label">
                                                {element.label}
                                            </div>

                                            <div class="dashboard-card-header-actions">
                                                <div class="dashboard-card-header-action" onClick={() => refExpanded.value = false}>
                                                    <i class="material-symbols-outlined">close</i>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="dashboard-expanded-chart">
                                            {renderExpandedVisualization(element, result, timeElement)}
                                        </div>

                                        {[ "line", "bar", "stacked-bar", "heatmap" ].includes(element.graphType || "line") && renderDrilldownTable()}
                                    </div>
                                </div>
                            )}
                        </div>
                    );

                default:
                    throw new Error(`Invalid type "${result["type"]}".`);
            }
        };
    },
})
